/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBody } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import BackgroundBox from 'shared/components/BackgroundBox';
import { LabelIcon } from 'shared/components/Labels';
import { Button, ButtonLabelIcon } from 'shared/components/Buttons';
import AddNotify from 'shared/components/AddNotify';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { AddFormModal } from 'shared/components/SharedStyles/SharedStyles';

import { setCurrentNotification } from 'redux/actions/notificationActions';
import { selectCurrentClient } from 'redux/actions/clientActions';

import { ALERT_TYPE_ERROR } from 'shared/utils';
import { colors } from 'shared/defaultTheme';

import { NOTIFICATION_TYPES } from 'shared/constants/contants';
import { showAlert } from 'redux/actions/alertActions';
import useNotification from 'hooks/useNotification';
import NotificationsTable from './NotificationsTable';

const NotificationSectionCard = ({ className, clients }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const { isDeleteClientFromHome } = useSelector((state) => state.clientState);
  const [openFormModal, setOpenFormModal] = useState(false);
  const { mainColor } = theme;
  const [activeTab, setActiveTab] = useState(NOTIFICATION_TYPES.SINGLES);
  const [notificationData, setNotificationData] = useState([]);

  const { massivesLoading, individualsLoading, getAllMassives, getAllIndividuals } =
    useNotification();

  const fetchNotifications = async () => {
    try {
      if (activeTab === NOTIFICATION_TYPES.SINGLES) {
        const data = await getAllIndividuals({
          limit: 8,
          skip: 0,
        });
        setNotificationData(data);
        return;
      }
      if (activeTab === NOTIFICATION_TYPES.MULTIPLES) {
        const data = await getAllMassives({
          limit: 8,
          skip: 0,
        });
        setNotificationData(data);
        return;
      }
    } catch (error) {
      setNotificationData([]);
      console.error(error);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };

  useEffect(() => {
    if (!isDeleteClientFromHome) return;
    (async () => {
      await fetchNotifications();
    })();
  }, [isDeleteClientFromHome]);

  useEffect(() => {
    (async () => {
      await fetchNotifications();
    })();
  }, [activeTab]);

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  return (
    <BackgroundBox
      className={classNames([
        'ml-background-dashboard background-box__flex-home d-flex flex-column',
        className,
      ])}
    >
      <div className="d-flex home__mb-notifi">
        <div
          onClick={() => {
            history.push('/notifications');
          }}
          aria-hidden="true"
        >
          <LabelIcon
            label={t('notifications_communication_title')}
            Icon={() => (
              <ConversationIcon fill={colors.white} className="label-icon__notification" />
            )}
            color={mainColor}
            className="ml-1 label-icon text-uppercase"
            classNameMain=""
            isDashboard
          />
        </div>

        <div className="d-flex w-100 justify-content-end">
          <ButtonLabelIcon
            id="add-icon-notification"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={() => {
              setOpenFormModal(true);
              dispatch(selectCurrentClient(undefined));
              dispatch(setCurrentNotification(null));
            }}
          />

          <ButtonLabelIcon
            id="refresh-icon-notification"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon"
            onClick={() => fetchNotifications()}
          />
        </div>
      </div>

      <NotificationsTable
        notificationData={notificationData}
        refreshNotificationsData={fetchNotifications}
        clients={clients}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isLoading={massivesLoading || individualsLoading}
      />
      {notificationData && notificationData.length > 0 && (
        <div className="action-btn">
          <Button
            className="home__btn text-uppercase"
            type="submit"
            onClick={() => history.push('/notifications')}
            label={t('general_see_all')}
            color="primary"
          />
        </div>
      )}

      <AddFormModal isOpen={openFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <div className="title-wrapper">
          <h1 className="title">{t('notification_title_add')}</h1>
        </div>
        <ModalBody>
          <AddNotify onCancel={onCloseFormModal} />
        </ModalBody>
      </AddFormModal>
    </BackgroundBox>
  );
};

NotificationSectionCard.defaultProps = {
  className: '',
};

NotificationSectionCard.propTypes = {
  className: PropTypes.string,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
};

export default NotificationSectionCard;
